import React from "react";
import { useQueryClient } from "react-query";
import humanize from "underscore.string/humanize";
import titleize from "underscore.string/titleize";
import { formatMoney } from "../utils/currrency";
import { 
  Buttons,
  ResponsiveTable,
  Paginator,
  ErrorBoundary,
  Loaders,
  Layouts,
} from "../components";
import { 
  useInvestorsList,
  useExportInvestorsList,
} from "../queries/investors";
import usePagination from "../hooks/usePagination";
import { CompanyContext } from "../contexts/CompanyContext";
import { useSessionContext } from "../contexts/SessionContext";

function InvestorsList() {
  const queryClient = useQueryClient();
  const pageConfig = usePagination();
  const { selectedCompany } = React.useContext(CompanyContext);
  const { isPermitted, ACTIONS } = useSessionContext();
  const canExport = isPermitted(ACTIONS.EXPORT_INVESTORS, selectedCompany.id)
  const investorList = useInvestorsList({...pageConfig.current, company_id: selectedCompany.slug})

  const tableData = React.useMemo(
    () => {
      if (investorList.isSuccess)
        return investorList.data.data
      else
        return []
    },
    [investorList]
  );


  const columns = React.useMemo(
    () => {
      if (investorList.isSuccess){
        return [
          { 
            Header: 'Investor Name',
            accessor: 'name',
            Cell: ({value}) => titleize(value),
          },
          {
            Header: 'Email',
            accessor: 'email',
          },
          {
            Header: 'Investor type',
            accessor: 'accredited',
            Cell: ({value}) => value === true ? 'Accredited' : 'Retail'
          },
          {
            Header: 'Investment amount',
            accessor: 'amount',
            Cell: ({value}) => formatMoney(value, {precision: 2}),
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: ({value}) => humanize(value),
          }
        ]
      }
      else{
        return []
      }
    },
    [investorList]
  );

  const _exportInvestorsList = useExportInvestorsList({company_id: selectedCompany.slug})
  const exportInvestors = async () => {
    queryClient.refetchQueries(
      ['investors', selectedCompany.slug, 'export'],
      {
        enabled: true,
      }
    );
  }

  if (investorList.isLoading || tableData === undefined)
    return <Loaders.ListPageLoader />

  return (
    <ErrorBoundary>
      <Layouts.PageHeader
        left="Investors"
        right={canExport ? <Buttons.TextButton title='Export All' onClick={exportInvestors}/> : null}
        tooltipDesc="All the investors in this company."
      />
      <div className="px-3">
        <Paginator
          theme='dark'
          onPage={pageConfig.setPage}
          meta={investorList.data.pagy}
        />
        <ResponsiveTable title="Investors" columns={columns} data={tableData} loading={investorList.isLoading} />
      </div>
    </ErrorBoundary>
  );
}

InvestorsList.defaultProps = {};

InvestorsList.propTypes = {};

export default InvestorsList;